/**
 * Available Backend Brand Identifiers:
 * - "LifeEssentialsClub" (support@lifeessentials.club)
 * - "TomMillerServices" (contact@tommillerservices.com)
 * - "VanguardIQ" (office@vanguardiq.com.au)
 *
 * Each brand has its own email templates, logos, and sender configurations
 * on the backend. The brandIdentifier must match exactly.
 */

export interface BrandConfig {
  // Core Brand Identity
  siteName: string;                // Main site/brand name displayed in headers and titles
  tagline: string;                 // Short brand slogan or value proposition
  description: string;             // Longer brand description for about sections and meta tags

  // Technical Configuration
  tenantId: string;                // Unique MongoDB tenant ID for data isolation
  apiUrl?: string;                 // Optional override for GraphQL API endpoint
  brandIdentifier: string;         // Backend brand identifier for email templates and notifications

  // Logo and Images
  logo: {
    src: string;                  // Logo image URL or emoji/icon
    alt: string;                  // Alt text for accessibility
    favicon: string;              // Browser favicon path
  };

  // Contact Information
  contact: {
    businessType: string;         // Type of business (e.g., "Business & Blockchain Solutions")
    industry: string;             // Industry category (e.g., "Technology")
    email?: string;               // Primary contact email
    phone?: string;               // Primary contact phone
    address?: string;             // Business physical address
    contactName?: string;         // Primary contact person name
    businessName?: string;        // Legal business name
  };

  // Color Palette
  colors: {
    // Additional accent colors (used throughout the app)
    accentBlue?: string;          // Secondary blue accent color
    primaryBlue?: string;         // Main blue for CTAs and highlights
    primaryBlueHover?: string;    // Hover state for primary blue elements
    starYellow?: string;          // Color for star/favorite icons
    starYellowHover?: string;     // Hover state for star icons
    successGreen?: string;        // Success indicators and checkmarks
    purpleAccent?: string;        // Purple accent for special highlights

    // Email module specific colors
    taskCardBg?: string;          // Background for task cards in task lists
    emailPreviewBg?: string;      // Background for email content preview areas
    darkCardBg?: string;          // Dark background for cards in dark theme
    darkBorderSubtle?: string;    // Subtle borders in dark theme

    // Highlight colors (for text highlighting and badges)
    highlightYellow?: string;     // Yellow highlight background
    highlightGreen?: string;      // Green highlight background
    highlightBlue?: string;       // Blue highlight background
    highlightPurple?: string;     // Purple highlight background
    highlightRed?: string;        // Red highlight background

    badges?: any;                 // Badge color schemes with bg, color, and border

    // Primary brand colors
    primary: string;              // Main brand color (buttons, links, primary actions)
    primaryHover: string;         // Hover state for primary color elements
    secondary: string;            // Secondary brand color (secondary actions)
    secondaryHover: string;       // Hover state for secondary color elements

    // Accent colors
    accent: string;               // Accent color for highlights and special elements
    accentLight: string;          // Lighter variant of accent color

    // Background colors
    background: {
      main: string;               // Main page background color
      light: string;              // Light background for sections
      card: string;               // Card/panel background color
      overlay: string;            // Modal/overlay background with transparency
      surface: string;            // Surface color for elevated elements
      darkSurface?: string;       // Dark theme surface color
      cardGradient?: string;      // Gradient background for cards in dark theme
      darkOverlay?: string;       // Dark overlay with transparency
      taskCard?: string;          // Task item background in task lists
      previewBg?: string;         // Email/content preview area background
    };

    // Status colors
    status: {
      success: string;            // Success states, confirmations, completed items
      warning: string;            // Warning states, caution indicators
      error: string;              // Error states, failed operations, deletions
      info: string;               // Informational states, hints, tips
      draft?: string;             // Draft email/document status
      sent?: string;              // Sent email status
      failed?: string;            // Failed email/operation status
      scheduled?: string;         // Scheduled email/task status
      inbound?: string;           // Inbound/received email status
    };

    // Text colors
    text: {
      primary: string;            // Primary text color (headings, important text)
      secondary: string;          // Secondary text color (body text)
      muted: string;              // Muted text (hints, placeholders, less important)
      inverse: string;            // Inverse text (white text on dark backgrounds)
      primaryDark?: string;       // Primary text for dark theme
      secondaryDark?: string;     // Secondary text for dark theme
      mutedDark?: string;         // Muted text for dark theme
    };

    // Border colors
    border: {
      light: string;              // Light borders (subtle separators)
      medium: string;             // Medium borders (standard borders)
      dark: string;               // Dark borders (emphasized borders)
      darkCard?: string;          // Card borders in dark theme
    };
  };

  // Typography
  fonts: {
    heading: string;              // Font family for headings (h1-h6)
    body: string;                 // Font family for body text and UI elements
  };

  // Component styling
  components: {
    button: {
      primaryBg: string;          // Primary button background color
      primaryHover: string;       // Primary button hover background
      secondaryBg: string;        // Secondary button background color
      secondaryHover: string;     // Secondary button hover background
      whiteBg?: string;           // White button background (for dark backgrounds)
      whiteText?: string;         // White button text color
      whiteHover?: string;        // White button hover background
      blueBg?: string;            // Blue button background (email module)
      blueHover?: string;         // Blue button hover background
    };
    tooltip?: {
      bg: string;                 // Tooltip background color
      color: string;              // Tooltip text color
      errorBg?: string;           // Error tooltip background
      darkBg?: string;            // Dark theme tooltip background
    };
    menu?: {
      bg: string;                 // Dropdown menu background
      itemBg: string;             // Menu item background
      itemColor: string;          // Menu item text color
      itemHoverBg: string;        // Menu item hover background
      itemHoverColor: string;     // Menu item hover text color
      borderColor: string;        // Menu border color
    };
    card: {
      bg: string;                 // Card background color
      border: string;             // Card border color
      shadow: string;             // Card shadow (elevation)
      darkShadow?: string;        // Card shadow in dark theme
      backdropFilter?: string;    // Backdrop filter for glass effect
      hoverShadow?: string;       // Card shadow on hover
    };
    navbar: {
      bg: string;                 // Navigation bar background
      text: string;               // Navigation bar text color
      shadow: string;             // Navigation bar shadow
    };
    footer: {
      bg: string;                 // Footer background color
      text: string;               // Footer text color
      linkColor: string;          // Footer link color
      linkHover: string;          // Footer link hover color
    };
    form: {
      bg: string;                 // Form background color
      fieldBg: string;            // Input field background
      fieldBorder: string;        // Input field border
      fieldBorderFocus: string;   // Input field border when focused
      fieldShadow: string;        // Input field shadow
      fieldShadowFocus: string;   // Input field shadow when focused
      labelColor: string;         // Form label text color
      placeholderColor: string;   // Input placeholder text color
    };
  };

  // Business specific
  membership: {
    totalLegacyMemberships: number;  // Total number of legacy members
    memberBenefits: {
      discount: string;               // Member discount description
      features: string[];             // List of member benefits/features
    };
  };

}

// VanguardIQ Projects Configuration - Citrus Splash Purple/Orange Theme
export const brandConfig: BrandConfig = {
  // Core Brand Identity
  siteName: "VanguardIQ Projects",
  tagline: "Collaboration & Business Intelligence",
  description: "VanguardiQ Back Office - Our collaboration platform for staff and active partners to track business operations and collaborate together.",

  // Technical Configuration
  tenantId: "699bf5a9d2b633a63c64ecc3",
  brandIdentifier: "VanguardIQ",

  // Logo and Images
  logo: {
    src: "🔷", // Clean geometric icon
    alt: "VanguardIQ Projects Logo",
    favicon: "%PUBLIC_URL%/favicon.ico"
  },

  // Contact Information
  contact: {
    businessType: "Business Intelligence & Automation",
    industry: "Technology",
    email: "office@vanguardiq.com.au",
    phone: "+61 458944327",
    address: "Professional Services, Australia",
    contactName: "VanguardIQ Team",
    businessName: "VanguardIQ"
  },

  // Color Palette - Citrus Splash Purple/Orange (VanguardIQ)
  colors: {
    // Primary brand colors (Soft Purple)
    primary: "#CB6BE6",        // Soft purple - bold & innovative
    primaryHover: "#B355D9",   // Darker purple on hover
    secondary: "#FFA500",      // Orange - energetic & warm
    secondaryHover: "#E6940A", // Darker orange

    // Accent colors (Citrus gradients)
    accent: "#FF4500",         // Orange-red accent
    accentLight: "#FFD700",    // Gold accent

    // Background colors (Dark gradient theme)
    background: {
      main: "#1A202C",         // Dark gray background
      light: "#FFFEF8",        // Off-white warm
      card: "#FFFFFF",         // White cards
      overlay: "rgba(203, 107, 230, 0.05)", // Light purple overlay
      surface: "#FFFFFF",      // White surface
      darkSurface: "#2D3748",
      cardGradient: "linear-gradient(135deg, rgba(203, 107, 230, 0.03) 0%, rgba(255, 165, 0, 0.03) 25%, rgba(255, 69, 0, 0.03) 50%, rgba(255, 215, 0, 0.03) 75%, rgba(255, 255, 0, 0.03) 100%)",
      darkOverlay: "rgba(203, 107, 230, 0.1)",
      taskCard: "#F7FAFC",
      previewBg: "#FAFAFA"
    },

    // Status colors (Citrus-inspired)
    status: {
      success: "#32CD32",      // Lime green
      warning: "#FFD700",      // Gold
      error: "#FF4500",        // Orange-red
      info: "#CB6BE6",         // Primary purple
      draft: "#718096",
      sent: "#48BB78",
      failed: "#F56565",
      scheduled: "#4299E1",
      inbound: "#805AD5"
    },

    // Text colors
    text: {
      primary: "#2D2D2D",      // Light mode
      secondary: "#5D5D5D",
      muted: "#8D8D8D",
      inverse: "#FFFFFF",
      primaryDark: "#F7FAFC",  // Dark mode
      secondaryDark: "#CBD5E0",
      mutedDark: "#718096"
    },

    // Border colors (Warm & energetic)
    border: {
      light: "rgba(255, 215, 0, 0.2)",
      medium: "rgba(255, 165, 0, 0.3)",
      dark: "rgba(255, 69, 0, 0.4)",
      darkCard: "rgba(203, 107, 230, 0.3)"
    },

    // Additional colors
    accentBlue: "#CB6BE6",
    primaryBlue: "#CB6BE6",
    primaryBlueHover: "#B355D9",
    starYellow: "#FFD700",
    starYellowHover: "#FFA500",
    successGreen: "#32CD32",
    purpleAccent: "#CB6BE6",

    // Email module specific colors
    taskCardBg: "rgba(45, 45, 55, 0.3)",
    emailPreviewBg: "rgba(45, 45, 55, 0.3)",
    darkCardBg: "rgba(26, 32, 44, 0.9)",
    darkBorderSubtle: "rgba(203, 107, 230, 0.05)",
    highlightYellow: "#FEF3C7",
    highlightGreen: "#D1FAE5",
    highlightBlue: "#E9D5FF",
    highlightPurple: "#E9D5FF",
    highlightRed: "#FEE2E2",

    // Badge colors with transparency (Citrus-inspired)
    badges: {
      blue: {
        bg: "rgba(203, 107, 230, 0.2)",
        color: "#CB6BE6",
        border: "rgba(203, 107, 230, 0.3)"
      },
      green: {
        bg: "rgba(50, 205, 50, 0.2)",
        color: "#32CD32",
        border: "rgba(50, 205, 50, 0.3)"
      },
      purple: {
        bg: "rgba(203, 107, 230, 0.2)",
        color: "#CB6BE6",
        border: "rgba(203, 107, 230, 0.3)"
      },
      yellow: {
        bg: "rgba(255, 215, 0, 0.2)",
        color: "#FFD700",
        border: "rgba(255, 215, 0, 0.3)"
      },
      gray: {
        bg: "rgba(93, 93, 93, 0.2)",
        color: "#5D5D5D",
        border: "rgba(93, 93, 93, 0.3)"
      },
      orange: {
        bg: "rgba(255, 165, 0, 0.2)",
        color: "#FFA500",
        border: "rgba(255, 165, 0, 0.3)"
      },
      teal: {
        bg: "rgba(255, 215, 0, 0.2)",
        color: "#FFD700",
        border: "rgba(255, 215, 0, 0.3)"
      },
      cyan: {
        bg: "rgba(255, 69, 0, 0.2)",
        color: "#FF4500",
        border: "rgba(255, 69, 0, 0.3)"
      }
    }
  },

  // Typography (Charter serif + Moderat sans)
  fonts: {
    heading: "Charter, ui-serif, Georgia, Cambria, 'Times New Roman', Times, serif",
    body: "Moderat, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif"
  },

  // Component styling (VanguardIQ purple/orange theme)
  components: {
    button: {
      primaryBg: "#CB6BE6",
      primaryHover: "#B355D9",
      secondaryBg: "rgba(255, 165, 0, 0.1)",
      secondaryHover: "rgba(255, 165, 0, 0.15)",
      whiteBg: "#FFFFFF",
      whiteText: "#CB6BE6",
      whiteHover: "#FFFEF8",
      blueBg: "#CB6BE6",
      blueHover: "#B355D9"
    },
    tooltip: {
      bg: "#2D3748",
      color: "#FFFFFF",
      errorBg: "#E53E3E",
      darkBg: "#1A202C"
    },
    menu: {
      bg: "rgba(45, 55, 72, 0.98)",
      itemBg: "transparent",
      itemColor: "#CBD5E0",
      itemHoverBg: "rgba(203, 107, 230, 0.2)",
      itemHoverColor: "#FFFFFF",
      borderColor: "rgba(203, 107, 230, 0.3)"
    },
    card: {
      bg: "#FFFFFF",
      border: "rgba(255, 215, 0, 0.2)",
      shadow: "0 1px 3px rgba(255, 69, 0, 0.1), 0 1px 2px rgba(255, 165, 0, 0.06)",
      darkShadow: "0 4px 25px 0 rgba(203, 107, 230, 0.08)",
      backdropFilter: "blur(8px)",
      hoverShadow: "0 10px 40px rgba(203, 107, 230, 0.15)"
    },
    navbar: {
      bg: "#1A202C",
      text: "#FFFFFF",
      shadow: "0 1px 3px rgba(0, 0, 0, 0.2)"
    },
    footer: {
      bg: "#1A202C",
      text: "#A0AEC0",
      linkColor: "#E0A3F5",
      linkHover: "#CB6BE6"
    },
    form: {
      bg: "#FFFFFF",
      fieldBg: "#FFFEF8",
      fieldBorder: "rgba(255, 165, 0, 0.3)",
      fieldBorderFocus: "#CB6BE6",
      fieldShadow: "0 1px 2px rgba(255, 69, 0, 0.05)",
      fieldShadowFocus: "0 0 0 3px rgba(203, 107, 230, 0.1), 0 1px 2px rgba(255, 69, 0, 0.05)",
      labelColor: "#2D2D2D",
      placeholderColor: "#8D8D8D"
    }
  },

  // Business specific
  membership: {
    totalLegacyMemberships: 500,
    memberBenefits: {
      discount: "Professional rates",
      features: [
        "Business Intelligence",
        "Digital Automation",
        "Project Management",
        "Team Collaboration"
      ]
    }
  },

};

// Theme-specific color configurations
export const lightTheme = {
  // Primary brand colors
  primary: "#CB6BE6",
  primaryHover: "#B355D9",
  secondary: "#FFA500",
  secondaryHover: "#E6940A",

  // Accent colors
  accent: "#FF4500",
  accentLight: "#FFD700",

  // Background colors (Light theme)
  background: {
    main: "#FFFFFF",
    light: "#FFFEF8",
    card: "#FFFFFF",
    overlay: "rgba(203, 107, 230, 0.05)",
    surface: "#FFFFFF",
    cardGradient: "linear-gradient(135deg, #FFFFFF 0%, #FFFEF8 100%)",
    darkOverlay: "rgba(0, 0, 0, 0.1)",
    taskCard: "#F7FAFC",
    previewBg: "#FAFAFA"
  },

  // Text colors (Light theme)
  text: {
    primary: "#2D2D2D",
    secondary: "#5D5D5D",
    muted: "#8D8D8D",
    inverse: "#FFFFFF",
    primaryDark: "#2D2D2D",
    secondaryDark: "#5D5D5D",
    mutedDark: "#8D8D8D"
  },

  // Border colors (Light theme)
  border: {
    light: "rgba(255, 215, 0, 0.2)",
    medium: "rgba(255, 165, 0, 0.3)",
    dark: "rgba(255, 69, 0, 0.4)",
    darkCard: "rgba(255, 215, 0, 0.2)"
  },

  // Component colors (Light theme)
  components: {
    card: {
      bg: "#FFFFFF",
      border: "rgba(255, 215, 0, 0.2)",
      shadow: "0 1px 3px rgba(255, 69, 0, 0.1), 0 1px 2px rgba(255, 165, 0, 0.06)",
      darkShadow: "0 1px 3px rgba(255, 69, 0, 0.1), 0 1px 2px rgba(255, 165, 0, 0.06)",
      hoverShadow: "0 10px 40px rgba(203, 107, 230, 0.15)",
      backdropFilter: "none"
    },
    navbar: {
      bg: "#FFFFFF",
      text: "#2D2D2D",
      shadow: "0 1px 3px rgba(0, 0, 0, 0.1)"
    },
    footer: {
      bg: "#FFFEF8",
      text: "#5D5D5D",
      linkColor: "#CB6BE6",
      linkHover: "#B355D9"
    },
    button: {
      primaryBg: "#CB6BE6",
      primaryHover: "#B355D9",
      secondaryBg: "rgba(255, 165, 0, 0.1)",
      secondaryHover: "rgba(255, 165, 0, 0.15)",
      whiteBg: "#2D2D2D",
      whiteText: "#FFFFFF",
      whiteHover: "#4A5568",
      blueBg: "#CB6BE6",
      blueHover: "#B355D9"
    }
  }
};

export const darkTheme = {
  // Primary brand colors
  primary: "#CB6BE6",
  primaryHover: "#B355D9",
  secondary: "#FFA500",
  secondaryHover: "#E6940A",

  // Accent colors
  accent: "#FF4500",
  accentLight: "#FFD700",

  // Background colors (Dark theme)
  background: {
    main: "#1A202C",
    light: "#FFFEF8",
    card: "#FFFFFF",
    overlay: "rgba(203, 107, 230, 0.05)",
    surface: "#FFFFFF",
    darkSurface: "#2D3748",
    cardGradient: "linear-gradient(135deg, rgba(203, 107, 230, 0.03) 0%, rgba(255, 165, 0, 0.03) 25%, rgba(255, 69, 0, 0.03) 50%, rgba(255, 215, 0, 0.03) 75%, rgba(255, 255, 0, 0.03) 100%)",
    darkOverlay: "rgba(203, 107, 230, 0.1)",
    taskCard: "rgba(45, 55, 72, 0.3)",
    previewBg: "rgba(45, 55, 72, 0.3)"
  },

  // Text colors (Dark theme)
  text: {
    primary: "#F7FAFC",
    secondary: "#CBD5E0",
    muted: "#718096",
    inverse: "#1A202C",
    primaryDark: "#F7FAFC",
    secondaryDark: "#CBD5E0",
    mutedDark: "#718096"
  },

  // Border colors (Dark theme)
  border: {
    light: "rgba(203, 107, 230, 0.1)",
    medium: "rgba(203, 107, 230, 0.2)",
    dark: "rgba(203, 107, 230, 0.3)",
    darkCard: "rgba(203, 107, 230, 0.3)"
  },

  // Component colors (Dark theme)
  components: {
    card: {
      bg: "#FFFFFF",
      border: "rgba(203, 107, 230, 0.2)",
      shadow: "0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)",
      darkShadow: "0 4px 25px 0 rgba(203, 107, 230, 0.08)",
      hoverShadow: "0 10px 40px rgba(203, 107, 230, 0.15)",
      backdropFilter: "blur(8px)"
    },
    navbar: {
      bg: "#1A202C",
      text: "#FFFFFF",
      shadow: "0 1px 3px rgba(0, 0, 0, 0.2)"
    },
    footer: {
      bg: "#1A202C",
      text: "#A0AEC0",
      linkColor: "#E0A3F5",
      linkHover: "#CB6BE6"
    },
    button: {
      primaryBg: "#CB6BE6",
      primaryHover: "#B355D9",
      secondaryBg: "rgba(255, 165, 0, 0.1)",
      secondaryHover: "rgba(255, 165, 0, 0.15)",
      whiteBg: "#FFFFFF",
      whiteText: "#CB6BE6",
      whiteHover: "#FFFEF8",
      blueBg: "#CB6BE6",
      blueHover: "#B355D9"
    },
    form: {
      bg: "rgba(26, 32, 44, 0.8)",
      fieldBg: "rgba(45, 55, 72, 0.8)",
      fieldBorder: "rgba(203, 107, 230, 0.2)",
      fieldBorderFocus: "#CB6BE6",
      fieldShadow: "0 1px 2px rgba(0, 0, 0, 0.3)",
      fieldShadowFocus: "0 0 0 3px rgba(203, 107, 230, 0.2), 0 1px 2px rgba(0, 0, 0, 0.3)",
      labelColor: "#F7FAFC"
    }
  }
};

// Helper function with color mode support
export const getColor = (path: string, colorMode?: 'light' | 'dark'): string => {
  const mode = colorMode || (localStorage.getItem('chakra-ui-color-mode') as 'light' | 'dark') || 'dark';
  const theme = mode === 'light' ? lightTheme : darkTheme;

  const keys = path.split(".");

  // Special handling for component paths
  if (keys[0] === "components") {
    let value: any = theme;
    for (const key of keys) {
      value = value[key];
      if (value === undefined) {
        let fallback: any = brandConfig.colors;
        const colorKeys = keys.slice(1);
        for (const key of colorKeys) {
          fallback = fallback[key];
          if (fallback === undefined) break;
        }
        if (fallback !== undefined) return fallback;

        console.warn(`Color path "${path}" not found in theme config for mode: ${mode}`);
        return "#CB6BE6"; // Fallback to primary purple
      }
    }
    return value;
  }

  // Default handling for colors object
  let value: any = theme;
  for (const key of keys) {
    value = value[key];
    if (value === undefined) {
      let fallback: any = brandConfig.colors;
      for (const key of keys) {
        fallback = fallback[key];
        if (fallback === undefined) break;
      }
      if (fallback !== undefined) return fallback;

      console.warn(`Color path "${path}" not found in theme config for mode: ${mode}`);
      return "#CB6BE6"; // Fallback to primary purple
    }
  }

  return value;
};

export const getBrandValue = (path: string): any => {
  const keys = path.split(".");
  let value: any = brandConfig;

  for (const key of keys) {
    value = value[key];
    if (value === undefined) {
      console.warn(`Brand config path "${path}" not found`);
      return null;
    }
  }

  return value;
};

export const getComponent = (component: string, property: string, colorMode?: 'light' | 'dark'): string => {
  const mode = colorMode || (localStorage.getItem('chakra-ui-color-mode') as 'light' | 'dark') || 'dark';
  const theme = mode === 'light' ? lightTheme : darkTheme;

  const componentConfig = (theme.components as any)?.[component];
  if (!componentConfig) {
    const fallbackConfig = (brandConfig.components as any)[component];
    if (fallbackConfig) {
      const value = fallbackConfig[property];
      if (value) return value;
    }
    console.warn(`Component "${component}" not found in theme config for mode: ${mode}`);
    return "#CB6BE6";
  }

  const value = componentConfig[property];
  if (!value) {
    const fallbackConfig = (brandConfig.components as any)[component];
    if (fallbackConfig) {
      const fallbackValue = fallbackConfig[property];
      if (fallbackValue) return fallbackValue;
    }
    console.warn(`Property "${property}" not found for component "${component}" in mode: ${mode}`);
    return "#CB6BE6";
  }

  return value;
};


// CSS Custom Properties Generator
export const generateCSSCustomProperties = (): string => {
  const { colors } = brandConfig;

  return `
    :root {
      --brand-primary: ${colors.primary};
      --brand-primary-hover: ${colors.primaryHover};
      --brand-secondary: ${colors.secondary};
      --brand-secondary-hover: ${colors.secondaryHover};
      --brand-accent: ${colors.accent};
      --brand-accent-light: ${colors.accentLight};

      --brand-bg-main: ${colors.background.main};
      --brand-bg-light: ${colors.background.light};
      --brand-bg-card: ${colors.background.card};
      --brand-bg-overlay: ${colors.background.overlay};
      --brand-bg-surface: ${colors.background.surface};

      --brand-success: ${colors.status.success};
      --brand-warning: ${colors.status.warning};
      --brand-error: ${colors.status.error};
      --brand-info: ${colors.status.info};

      --brand-text-primary: ${colors.text.primary};
      --brand-text-secondary: ${colors.text.secondary};
      --brand-text-muted: ${colors.text.muted};
      --brand-text-inverse: ${colors.text.inverse};

      --brand-border-light: ${colors.border.light};
      --brand-border-medium: ${colors.border.medium};
      --brand-border-dark: ${colors.border.dark};

      --brand-font-heading: ${brandConfig.fonts.heading};
      --brand-font-body: ${brandConfig.fonts.body};
    }
  `;
};

export default brandConfig;
